import React, { FunctionComponent, useEffect, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import {
  AdminProductDistributionPlatformListing,
  DistributionPlatform,
  DistributionPlatformPermissionEnum,
} from 'src/api';
import ListDistributionInformations, { ExtendedDistributionInformation } from './list-distribution-informations';
import {
  hasDistributionEditInformationPermission,
  hasDistributionViewInformationPermission,
} from '../../../helpers/distributionPlatformPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import Button from 'src/ui/atoms/button';
import AddDistributionInformation from './add-distribution-information';
interface DistributionInformationProps {
  listings: AdminProductDistributionPlatformListing[];
  onDistributionInformationAdded: () => void;
}

const DistributionInformation: FunctionComponent<DistributionInformationProps> = ({
  listings,
  onDistributionInformationAdded,
}) => {
  const [distributionInformations, setDistributionInformations] = useState<ExtendedDistributionInformation[]>([]);
  const [hasViewDistributionInformationPermission, setHasViewDistributionInformationPermission] = useState(false);
  const [hasEditDistributionInformationPermission, setHasEditDistributionInformationPermission] = useState(false);
  const [distributionPlatformsWithEditPermission, setDistributionPlatformsWithEditPermission] = useState<
    DistributionPlatform[]
  >([]);
  const [isAddDistributionInformationModalOpen, setAddDistributionInformationModalOpen] = useState(false);

  const { currentUser } = useCurrentUserSelector();

  useEffect(() => {
    // check view permission only for the platforms that have been listed
    const relevantDistributionPlatforms = (currentUser?.associatedDistributionPlatforms || []).filter((platform) =>
      listings.some((listing) => listing.usesEffecta === true && listing.distributionPlatformId === platform.id),
    );
    setHasViewDistributionInformationPermission(
      hasDistributionViewInformationPermission(relevantDistributionPlatforms),
    );
    setHasEditDistributionInformationPermission(
      hasDistributionEditInformationPermission(relevantDistributionPlatforms),
    );

    // get platforms with edit permission that don't have distribution info.
    setDistributionPlatformsWithEditPermission(
      hasDistributionEditInformationPermission(relevantDistributionPlatforms)
        ? relevantDistributionPlatforms.filter((platform) => {
            return (
              platform.permissions.includes(
                DistributionPlatformPermissionEnum.EDIT_DISTRIBUTION_INFORMATION_AND_INVESTMENT_DOCUMENTS,
              ) &&
              listings.find((listing) => listing.distributionPlatformId === platform.id)?.distributionInformation ===
                null
            );
          })
        : [],
    );

    // get existing distribution informations
    setDistributionInformations(
      listings
        .filter((listing) => listing.distributionInformation !== null)
        .map((listing) => ({
          ...listing.distributionInformation,
          platformId: listing.distributionPlatformId,
          platformName: listing.distributionPlatformPlatformName,
          productDistributionPlatformListingId: listing.id,
        })),
    );
  }, [JSON.stringify(listings), JSON.stringify(currentUser?.associatedDistributionPlatforms)]);

  if (!hasViewDistributionInformationPermission) return null;

  return (
    <WideContent
      title={<Translate name="distributionInformation.title" />}
      ignorePageTitle
      titleSpacing="small"
      headerPanel={
        distributionPlatformsWithEditPermission.length > 0 && (
          <Button
            variant="primary"
            icon="plus"
            iconColor="white"
            onClick={() => setAddDistributionInformationModalOpen(true)}
          >
            <Translate name="distributionInformation.add" />
          </Button>
        )
      }
    >
      {distributionInformations.length === 0 && <Translate name="distributionInformation.noDistribution" />}
      {distributionInformations.length > 0 && (
        <ListDistributionInformations
          distributionInformations={distributionInformations}
          hasEditDistributionInformationPermission={hasEditDistributionInformationPermission}
          onDistributionInformationAdded={onDistributionInformationAdded}
          listings={listings}
          distributionPlatformsWithEditPermission={distributionPlatformsWithEditPermission}
        />
      )}
      {isAddDistributionInformationModalOpen && (
        <AddDistributionInformation
          onDistributionInformationAdded={() => {
            setAddDistributionInformationModalOpen(false);
            onDistributionInformationAdded();
          }}
          onClose={() => setAddDistributionInformationModalOpen(false)}
          listings={listings}
          distributionPlatformsWithEditPermission={distributionPlatformsWithEditPermission}
        ></AddDistributionInformation>
      )}
    </WideContent>
  );
};

export default DistributionInformation;
